import { createApi } from '@reduxjs/toolkit/query/react';

import { ICommentCourse } from '@/interfaces/comments';
import { enStatus, ICourse, IModule, IMyCourse } from '@/interfaces/course';
import { ILastLessonWatch } from '@/interfaces/lesson';
import { IPaginationParams, IPaginationResponse } from '@/interfaces/pagination';
import { IUpsell } from '@/interfaces/upsell';
import { API_BASE_URL } from '@/settings';

import { axiosBaseQuery } from './query';

export type Params = Partial<IPaginationParams & { hasFile: boolean; hasSavedLessons: boolean }>;

export type ModulesParams = {
  courseHash: string;
  params?: Params;
};

export const coursesApi = createApi({
  baseQuery: axiosBaseQuery(),
  reducerPath: 'coursesApi',
  endpoints(build) {
    return {
      getAllMyComments: build.query<
        IPaginationResponse<ICommentCourse[]>,
        { courseHash: string; params: Partial<IPaginationParams> }
      >({
        query: ({ courseHash, params }) => ({
          url: `/learner/course/${courseHash}/my-all-comments`,
          params
        })
      }),
      getProgress: build.query<IModule[], string>({
        query: courseHash => ({
          formatResponse: response => response.data.data,
          url: `/learner/course/${courseHash}/modules/v2`
        })
      }),
      getModules: build.query<IPaginationResponse<IModule[], Params>, ModulesParams>({
        forceRefetch({ currentArg, previousArg }) {
          return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
        },
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
        merge: (currCache, newCache) => {
          currCache.total_pages = newCache.total_pages;
          currCache.page = newCache.page;
          currCache.links = newCache.links;

          if (newCache.page.page === 1) {
            currCache.data = newCache.data;
          } else {
            currCache.data.push(...newCache.data);
          }
        },
        query: ({ courseHash, params }) => ({
          url: `/learner/course/${courseHash}/lessons/v2`,
          params
        })
      }),
      getLastLessonWatched: build.query<ILastLessonWatch, string | undefined>({
        query: courseHash => ({
          formatResponse: response => response.data.data,
          url: `/learner/course/${courseHash}/last-lesson-watched`
        })
      }),
      getCourse: build.query<ICourse, string | undefined>({
        query: courseHash => ({
          formatResponse: response => response.data.data,
          url: `/learner/course/${courseHash}`
        })
      }),
      getCourses: build.query<IPaginationResponse<IMyCourse[]>, Partial<IPaginationParams>>({
        forceRefetch({ currentArg, previousArg }) {
          return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
        },
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
        merge: (currCache, newCache) => {
          currCache.total_pages = newCache.total_pages;
          currCache.page = newCache.page;
          currCache.links = newCache.links;

          if (newCache.page.page === 1) {
            currCache.data = newCache.data;
          } else {
            currCache.data.push(...newCache.data);
          }
        },
        query: params => ({
          url: '/learner/course/search',
          params
        })
      }),
      getUpsell: build.query<IUpsell, string | undefined>({
        query: courseHash => ({
          formatResponse: response => response.data.data,
          url: `/learner/course/${courseHash}/upsell`
        })
      }),
      getCertificate: build.query<string, string | undefined>({
        query: courseHash => ({
          formatResponse: response => response.data.data?.certificate?.url,
          url: `/course/${courseHash}/certificate`
        }),
        async onQueryStarted(courseHash, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(
              coursesApi.util.updateQueryData('getCourse', courseHash, draft => {
                Object.assign(draft, { certificate_url: data });
              })
            );
          } catch {}
        }
      }),
      acceptTerm: build.mutation<{ course_status: enStatus }, { courseHash: string; accepted: boolean }>({
        query: ({ courseHash, accepted }) => ({
          formatResponse: response => response.data.data,
          baseURL: API_BASE_URL,
          url: `/learner/course/${courseHash}/accept-terms`,
          data: { accepted },
          method: 'post'
        }),
        async onQueryStarted({ courseHash }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(
              coursesApi.util.updateQueryData('getCourse', courseHash, draft => {
                Object.assign(draft, { status: data?.course_status });
              })
            );
          } catch {}
        }
      }),
      getCommunityUrl: build.query<string, string>({
        query: courseHash => ({
          formatResponse: response => response.data.url,
          url: `/learner/communities/${courseHash}`
        })
      })
    };
  }
});

export const {
  useGetCoursesQuery,
  useGetCourseQuery,
  useGetLastLessonWatchedQuery,
  useGetModulesQuery,
  useGetUpsellQuery,
  useGetCertificateQuery,
  useAcceptTermMutation,
  useGetAllMyCommentsQuery,
  useGetProgressQuery,
  useGetCommunityUrlQuery
} = coursesApi;
